import type {
  SelectFeatureTool as CesiumSelectFeatureTool,
  CesiumViewer,
  FeatureInfoType,
  Line,
  Point,
  Polygon,
  SelectFeature,
  Textbox,
  DrawingTools,
} from '@aus-platform/cesium';
import { findIndex, isEmpty, isUndefined } from 'lodash';
import { GeometryType } from '../../../../../shared/cesium';
import { BaseTool } from '../../base-tool/base-tool';
import { SelectToolEventType } from './enums';
import type { ISelectFeatureTool } from './select-feature-tool-interface';
import type { SelectToolEventListener } from './types';

export class SelectFeatureTool extends BaseTool implements ISelectFeatureTool {
  private _currentSelectedFeatures: (Line | Polygon | Point | Textbox)[] = [];
  private _selectFeature: SelectFeature;
  private _selectFeatureTool: CesiumSelectFeatureTool;
  private _viewer: CesiumViewer;
  private _isActive = false;

  constructor(viewer: CesiumViewer) {
    super(viewer);
    this._viewer = viewer;
    this._selectFeatureTool = this.viewer.selectTools.selectFeatureTool;
    this._selectFeature =
      this.viewer.selectTools.selectFeatureTool.selectFeature;
  }

  activate(selectOptions: Record<string, any>, enableDefaultListeners = true) {
    this._isActive = true;
    this.viewer.deactivateCurrentMapTool();
    if (enableDefaultListeners) {
      this.activateDefaultListeners();
    }
    this._selectFeatureTool.activateSelectFeature(selectOptions);
  }

  deactivate() {
    if (this.isDestroyed) {
      return;
    }

    this._isActive = false;
    this.deselectSelectedFeatures();
    super.deactivate();
    this._selectFeatureTool.destroy();
  }

  resetSelectedFeatures() {
    this._currentSelectedFeatures = [];
  }

  deleteSelectedFeatures() {
    this._selectFeature.deleteAllSelectedFeatures();
  }

  deselectSelectedFeatures() {
    this._selectFeature.deselectAllFeatures();
    this.resetSelectedFeatures();
  }

  // Cursor handlers.
  setOverrideCursorWait() {
    this._selectFeature.setOverrideCursorWait();
  }

  restoreOverrideCursor() {
    this._selectFeature.restoreOverrideCursor();
  }

  // Listeners.
  private _defaultSelectFeatureListener(featureInfoArray: FeatureInfoType[]) {
    if (!isUndefined(featureInfoArray) && !isEmpty(featureInfoArray)) {
      const featureInfo = featureInfoArray[0];
      switch (featureInfo.type.toString()) {
        case GeometryType.Polygon: {
          const polygon =
            this.viewer.drawingTools.polygonDrawingTools.getPolygon(
              featureInfo.id,
            );
          if (
            polygon &&
            findIndex(this._currentSelectedFeatures, { id: polygon.id }) === -1
          ) {
            this._currentSelectedFeatures.push(polygon);
          }
          break;
        }
        case GeometryType.Line: {
          const line = this.viewer.drawingTools.lineDrawingTools.getLine(
            featureInfo.id,
          );
          if (
            line &&
            findIndex(this._currentSelectedFeatures, { id: line.id }) === -1
          ) {
            this._currentSelectedFeatures.push(line);
          }
          break;
        }
        case GeometryType.Point: {
          const point = this.viewer.drawingTools.pointDrawingTools.getPoint(
            featureInfo.id,
          );
          if (
            point &&
            findIndex(this._currentSelectedFeatures, { id: point.id }) === -1
          ) {
            this._currentSelectedFeatures.push(point);
          }
          break;
        }
        case GeometryType.Textbox: {
          const textbox = this.viewer.textTool.textDraw.getTextboxById(
            featureInfo.id,
          );
          if (
            textbox &&
            findIndex(this._currentSelectedFeatures, { id: textbox.id }) === -1
          ) {
            this._currentSelectedFeatures.push(textbox);
          }
          break;
        }
      }
    }
  }

  private _defaultDeselectFeatureListener(featureInfoArray: FeatureInfoType[]) {
    const featureId = featureInfoArray?.[0]?.id;
    if (!featureId) {
      return;
    }

    const featureIndex = findIndex(this._currentSelectedFeatures, {
      id: featureId,
    });

    if (featureIndex !== -1) {
      this._currentSelectedFeatures.splice(featureIndex, 1);
    }
  }

  activateDefaultListeners() {
    this._selectFeatureTool.selectFeature.eventFeatureSelected.addEventListener(
      this._defaultSelectFeatureListener,
      this,
    );
    this._selectFeatureTool.selectFeature.eventFeatureDeselected.addEventListener(
      this._defaultDeselectFeatureListener,
      this,
    );
  }

  selectFeature(featureInfo: FeatureInfoType) {
    this._selectFeature.selectFeature(featureInfo);
  }

  removeDefaultListeners() {
    this._selectFeatureTool.selectFeature.eventFeatureSelected.removeEventListener(
      this._defaultSelectFeatureListener,
      this,
    );
    this._selectFeatureTool.selectFeature.eventFeatureDeselected.removeEventListener(
      this._defaultDeselectFeatureListener,
      this,
    );
  }

  addEventListener(
    type: SelectToolEventType,
    listener: SelectToolEventListener,
  ) {
    switch (type) {
      case SelectToolEventType.FeatureSelected: {
        return this._selectFeature.eventFeatureSelected.addEventListener(
          listener,
        );
      }
      case SelectToolEventType.FeatureDeselected: {
        return this._selectFeature.eventFeatureDeselected.addEventListener(
          listener,
        );
      }
      default: {
        throw new Error('Invalid event type');
      }
    }
  }

  removeEventListener(
    type: SelectToolEventType,
    listener: SelectToolEventListener,
  ) {
    switch (type) {
      case SelectToolEventType.FeatureSelected: {
        return this._selectFeature.eventFeatureSelected.removeEventListener(
          listener,
        );
      }
      case SelectToolEventType.FeatureDeselected: {
        return this._selectFeature.eventFeatureDeselected.removeEventListener(
          listener,
        );
      }
      default: {
        throw new Error('Invalid event type');
      }
    }
  }

  get selectedFeatures(): (Line | Polygon | Point | Textbox)[] {
    return this._currentSelectedFeatures;
  }

  get selectedFeatureCount(): number {
    return this._currentSelectedFeatures.length;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  get selectFeatureTool() {
    return this._selectFeatureTool;
  }

  get drawingTools(): DrawingTools {
    if (this.viewer.drawingTools) {
      throw Error('Drawing Tools are not initialized');
    }
    return this.viewer.drawingTools;
  }

  destroy() {
    if (this.isDestroyed) {
      return;
    }

    this._selectFeatureTool.destroy();
  }
}
